import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useWorkspaceStore, selectActiveWorkspace } from '@/stores/workspaceStore';
import { useUIStore } from '@/stores/uiStore';
import { aiApi, capabilitiesApi, type OnboardSuggestion, type OnboardSignal } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 'goal' | 'context' | 'analyzing' | 'review' | 'creating';

interface ReviewState {
  thread_name: string;
  thread_summary: string;
  capabilities: (OnboardSuggestion & { selected: boolean; id: string })[];
  signals: OnboardSignal[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  skill: 'Skill',
  knowledge: 'Knowledge',
  experience: 'Experience',
  tool: 'Tool',
  relationship: 'Relationship',
  credential: 'Credential',
  other: 'Other',
};

// ─── Step components ──────────────────────────────────────────────────────────

const StepGoal: React.FC<{
  goal: string;
  onChange: (v: string) => void;
  onNext: () => void;
}> = ({ goal, onChange, onNext }) => (
  <div className="flex flex-col gap-6">
    <div>
      <h2
        className="text-2xl font-bold mb-2"
        style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.04em' }}
      >
        What are you working toward?
      </h2>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
        Describe a goal, a problem you're solving, or a transition you're navigating. There's no wrong answer — this becomes the context for your first Thread.
      </p>
    </div>
    <textarea
      value={goal}
      onChange={(e) => onChange(e.target.value)}
      placeholder="e.g. I'm transitioning from software engineering to product management and want to understand my strengths and gaps..."
      rows={5}
      autoFocus
      className="w-full rounded-lg px-4 py-3 outline-none resize-none"
      style={{
        background: 'var(--bg-high)',
        border: '1px solid var(--border-mid)',
        color: 'var(--text)',
        fontFamily: 'var(--font-body)',
        fontSize: '1rem',
        lineHeight: 1.65,
      }}
      onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--crimson-border)'; }}
      onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-mid)'; }}
    />
    <button
      type="button"
      onClick={onNext}
      disabled={goal.trim().length < 10}
      className="btn-primary self-end"
    >
      Continue
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    </button>
  </div>
);

const StepContext: React.FC<{
  context: string;
  onChange: (v: string) => void;
  onBack: () => void;
  onAnalyze: () => void;
}> = ({ context, onChange, onBack, onAnalyze }) => (
  <div className="flex flex-col gap-6">
    <div>
      <h2
        className="text-2xl font-bold mb-2"
        style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.04em' }}
      >
        Share some context
      </h2>
      <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
        Paste your resume, LinkedIn summary, a job description you're targeting, or just describe your background in plain language. The more you share, the richer your starting map. You can skip this and still get a solid starting point.
      </p>
    </div>
    <textarea
      value={context}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Paste your resume, LinkedIn profile, job description, or anything relevant..."
      rows={10}
      className="w-full rounded-lg px-4 py-3 outline-none resize-none"
      style={{
        background: 'var(--bg-high)',
        border: '1px solid var(--border-mid)',
        color: 'var(--text)',
        fontFamily: 'var(--font-mono)',
        fontSize: '0.85rem',
        lineHeight: 1.6,
      }}
      onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--crimson-border)'; }}
      onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-mid)'; }}
    />
    <div className="flex items-center justify-between">
      <button type="button" onClick={onBack} className="btn-ghost">
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back
      </button>
      <button type="button" onClick={onAnalyze} className="btn-primary">
        Analyze
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </div>
);

const StepAnalyzing: React.FC = () => {
  const [phrase, setPhrase] = React.useState(0);
  const phrases = ['Identifying patterns…', 'Mapping capabilities…', 'Surfacing signals…', 'Building your Thread…'];

  React.useEffect(() => {
    const t = setInterval(() => setPhrase((p) => (p + 1) % phrases.length), 1800);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-16">
      <div
        className="w-12 h-12 rounded-full border-2 border-t-transparent animate-spin"
        style={{ borderColor: 'var(--crimson)', borderTopColor: 'transparent' }}
      />
      <div>
        <p
          className="text-base font-bold text-center"
          style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          {phrases[phrase]}
        </p>
        <p className="text-xs text-center mt-1" style={{ color: 'var(--text-muted)' }}>
          This takes a few seconds
        </p>
      </div>
    </div>
  );
};

const StepReview: React.FC<{
  review: ReviewState;
  onToggle: (id: string) => void;
  onBack: () => void;
  onConfirm: () => void;
}> = ({ review, onToggle, onBack, onConfirm }) => {
  const selectedCount = review.capabilities.filter((c) => c.selected).length;

  return (
    <div className="flex flex-col gap-6">
      {/* Thread name + summary */}
      <div
        className="rounded-lg px-5 py-4"
        style={{ background: 'var(--crimson-dim)', border: '1px solid var(--crimson-border)' }}
      >
        <p className="text-[10px] uppercase font-bold mb-1" style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.14em', color: 'var(--crimson-light)' }}>
          Thread
        </p>
        <h3
          className="text-lg font-bold mb-1"
          style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', textTransform: 'uppercase', letterSpacing: '0.04em' }}
        >
          {review.thread_name}
        </h3>
        <p className="text-sm" style={{ color: 'var(--text-dim)' }}>
          {review.thread_summary}
        </p>
      </div>

      {/* Capabilities */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase font-bold" style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.14em', color: 'var(--text-muted)' }}>
            Capabilities — check to include
          </p>
          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {selectedCount} of {review.capabilities.length} selected
          </span>
        </div>
        <div className="flex flex-col gap-2">
          {review.capabilities.map((cap) => (
            <button
              key={cap.id}
              type="button"
              onClick={() => onToggle(cap.id)}
              className="flex items-start gap-3 rounded-lg px-4 py-3 text-left transition-colors w-full"
              style={{
                background: cap.selected ? 'var(--bg-high)' : 'var(--bg-mid)',
                border: `1px solid ${cap.selected ? 'var(--border-mid)' : 'var(--border)'}`,
              }}
            >
              {/* Checkbox */}
              <div
                className="flex-shrink-0 w-4 h-4 mt-0.5 rounded flex items-center justify-center transition-colors"
                style={{
                  background: cap.selected ? 'var(--crimson)' : 'transparent',
                  border: `1.5px solid ${cap.selected ? 'var(--crimson)' : 'var(--border-mid)'}`,
                }}
              >
                {cap.selected && (
                  <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{cap.name}</span>
                  <span
                    className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded"
                    style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.08em', background: 'var(--bg-deep)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}
                  >
                    {TYPE_LABELS[cap.type] ?? cap.type}
                  </span>
                </div>
                <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--text-dim)' }}>
                  {cap.description}
                </p>
                {cap.tags.length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-1.5">
                    {cap.tags.map((tag) => (
                      <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-deep)', color: 'var(--text-faint)', border: '1px solid var(--border)' }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Signals */}
      {review.signals.length > 0 && (
        <div>
          <p className="text-[10px] uppercase font-bold mb-3" style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.14em', color: 'var(--text-muted)' }}>
            Signals — patterns we noticed
          </p>
          <div className="flex flex-col gap-2">
            {review.signals.map((sig, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-lg px-4 py-3"
                style={{ background: 'var(--bg-mid)', border: '1px solid var(--border)' }}
              >
                <div
                  className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2"
                  style={{ background: 'var(--crimson)' }}
                />
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{sig.name}</p>
                  <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-dim)' }}>{sig.description}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] mt-2 leading-relaxed" style={{ color: 'var(--text-faint)' }}>
            Signals are stored as observations, not capabilities. They'll surface again as patterns accumulate.
          </p>
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <button type="button" onClick={onBack} className="btn-ghost">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={selectedCount === 0}
          className="btn-primary"
        >
          Build my map
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const StepCreating: React.FC = () => (
  <div className="flex flex-col items-center justify-center gap-6 py-16">
    <div
      className="w-12 h-12 rounded-full border-2 border-t-transparent animate-spin"
      style={{ borderColor: 'var(--crimson)', borderTopColor: 'transparent' }}
    />
    <p
      className="text-base font-bold text-center"
      style={{ fontFamily: 'var(--font-display)', color: 'var(--text)', letterSpacing: '0.06em', textTransform: 'uppercase' }}
    >
      Building your map…
    </p>
  </div>
);

// ─── OnboardingPage ───────────────────────────────────────────────────────────

const OnboardingPage: React.FC = () => {
  const history = useHistory();
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);
  const workspaceId = activeWorkspace?.id ?? null;
  const upsertCapability = useWorkspaceStore((s) => s.upsertCapability);
  const addToast = useUIStore((s) => s.addToast);

  const [step, setStep] = useState<Step>('goal');
  const [goal, setGoal] = useState('');
  const [context, setContext] = useState('');
  const [review, setReview] = useState<ReviewState | null>(null);

  const handleAnalyze = useCallback(async () => {
    if (!workspaceId) return;
    setStep('analyzing');
    try {
      const result = await aiApi.onboard(workspaceId, goal, context || undefined);
      setReview({
        thread_name: result.thread_name,
        thread_summary: result.thread_summary,
        capabilities: result.capabilities.map((c, i) => ({
          ...c,
          selected: true,
          id: `suggestion-${i}`,
          tags: c.tags ?? [],
        })),
        signals: result.signals ?? [],
      });
      setStep('review');
    } catch (err) {
      console.error('[Onboarding] analyze failed', err);
      addToast({ title: 'Analysis failed', description: err instanceof Error ? err.message : 'Try again', variant: 'error' });
      setStep('context');
    }
  }, [workspaceId, goal, context, addToast]);

  const handleToggle = useCallback((id: string) => {
    setReview((prev) =>
      prev
        ? { ...prev, capabilities: prev.capabilities.map((c) => c.id === id ? { ...c, selected: !c.selected } : c) }
        : prev
    );
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!workspaceId || !review) return;
    setStep('creating');
    const selected = review.capabilities.filter((c) => c.selected);
    const VALID_TYPES = new Set(['skill', 'knowledge', 'experience', 'tool', 'relationship', 'credential', 'domain']);
    try {
      for (const cap of selected) {
        const capType = VALID_TYPES.has(cap.type)
          ? (cap.type as 'skill' | 'knowledge' | 'experience' | 'tool' | 'relationship' | 'credential' | 'domain')
          : 'skill';
        const created = await capabilitiesApi.create(workspaceId, {
          workspaceId,
          title: cap.name,
          summary: cap.description,
          content: '',
          type: capType,
          status: 'active',
          tags: cap.tags,
          strength: 50,
          evidence: [],
          collectionIds: [],
          metadata: {},
        });
        upsertCapability(created);
      }
      addToast({ title: `${selected.length} capabilities added`, variant: 'success' });
      history.replace('/graph');
    } catch (err) {
      console.error('[Onboarding] create failed', err);
      addToast({ title: 'Could not create capabilities', description: err instanceof Error ? err.message : 'Try again', variant: 'error' });
      setStep('review');
    }
  }, [workspaceId, review, upsertCapability, addToast, history]);

  return (
    <div
      className="flex flex-col items-center justify-start px-4"
      style={{ background: 'var(--bg-base)', height: '100vh', paddingTop: '3rem', paddingBottom: '3rem', overflowY: 'auto', overflowX: 'hidden' }}
    >
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="mb-10">
          <p
            className="text-xs uppercase font-bold tracking-widest mb-3"
            style={{ fontFamily: 'var(--font-display)', color: 'var(--crimson)', letterSpacing: '0.2em' }}
          >
            Signature
          </p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Let's build your starting map. This takes about two minutes.
          </p>
        </div>

        {/* Step progress */}
        {(step === 'goal' || step === 'context' || step === 'review') && (
          <div className="flex items-center gap-2 mb-8">
            {(['goal', 'context', 'review'] as const).map((s, i) => (
              <React.Fragment key={s}>
                <div
                  className="w-2 h-2 rounded-full transition-colors"
                  style={{
                    background: step === s
                      ? 'var(--crimson)'
                      : i < (['goal', 'context', 'review'] as const).indexOf(step)
                      ? 'var(--text-dim)'
                      : 'var(--bg-high)',
                    border: `1px solid ${step === s ? 'var(--crimson)' : 'var(--border)'}`,
                  }}
                />
                {i < 2 && <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Step content */}
        {step === 'goal' && (
          <StepGoal
            goal={goal}
            onChange={setGoal}
            onNext={() => setStep('context')}
          />
        )}
        {step === 'context' && (
          <StepContext
            context={context}
            onChange={setContext}
            onBack={() => setStep('goal')}
            onAnalyze={handleAnalyze}
          />
        )}
        {step === 'analyzing' && <StepAnalyzing />}
        {step === 'review' && review && (
          <StepReview
            review={review}
            onToggle={handleToggle}
            onBack={() => setStep('context')}
            onConfirm={handleConfirm}
          />
        )}
        {step === 'creating' && <StepCreating />}
      </div>
    </div>
  );
};

export default OnboardingPage;
